import React from 'react';
import { pdf, DocumentProps } from '@react-pdf/renderer';
import JSZip from 'jszip';

import { SurveillanceReport } from './SurveillanceReport';
import { fetchSurveillanceReportData } from './fetchSurveillanceReportData';
import { SurveillanceReportOptions } from './surveillanceTypes';
import { ReportSortOption } from './combinedSort';
import { saveBlob } from '../saveBlob';

// ═══════════════════════════════════════════════════════════════════
// Descarga del informe del Recorregut de Vigilància (2026-07-27).
// Espejo exacto de `generateCombinedPDF`: 1 recorrido → PDF, N → ZIP.
// La diferencia es qué se pinta, que viaja en `options`.
// ═══════════════════════════════════════════════════════════════════

const triggerDownload = async (blob: Blob, filename: string): Promise<void> => {
  await saveBlob(blob, filename);
};

const sanitize = (roid: string, fallback: string): string =>
  (roid || fallback).replace(/[^\w.-]+/g, '_');

async function buildBlob(
  routeId: string,
  options: SurveillanceReportOptions,
  cloudName: string,
  sort?: ReportSortOption,
  onProgress?: (current: number, total: number) => void,
): Promise<{ blob: Blob; roid: string } | null> {
  const data = await fetchSurveillanceReportData({
    routeId,
    options,
    cloudName,
    sort,
    onProgress,
  });
  if (!data) return null;
  const element = React.createElement(SurveillanceReport, {
    data,
  }) as React.ReactElement<DocumentProps>;
  const blob = await pdf(element).toBlob();
  return { blob, roid: data.roid };
}

/**
 * Genera el informe del recorrido con las opciones elegidas en el modal.
 * - 1 recorrido  → descarga un único PDF.
 * - N recorridos → descarga un ZIP con un PDF por recorrido.
 *
 * `routeIds` son ids de la ENTIDAD `SurveillanceRoute` (las filas del
 * histórico publican `sr.id`, no el id de su Order — al revés que genéricas y
 * campañas).
 */
export async function generateSurveillancePDF(params: {
  routeIds: string[];
  options: SurveillanceReportOptions;
  cloudName: string;
  /** Orden de las actuaciones cuando se piden con detalle completo. */
  sort?: ReportSortOption;
  onProgress?: (current: number, total: number) => void;
}): Promise<void> {
  const { routeIds, options, cloudName, sort, onProgress } = params;
  if (routeIds.length === 0) return;

  if (routeIds.length === 1) {
    // Con un solo recorrido el progreso útil es el de sus actuaciones, así que
    // el callback se pasa hacia dentro.
    const res = await buildBlob(
      routeIds[0],
      options,
      cloudName,
      sort,
      onProgress,
    );
    if (res) {
      triggerDownload(
        res.blob,
        `informe-${sanitize(res.roid, routeIds[0])}.pdf`,
      );
    }
    return;
  }

  const zip = new JSZip();
  let done = 0;
  for (const id of routeIds) {
    const res = await buildBlob(id, options, cloudName, sort);
    if (res) {
      zip.file(`informe-${sanitize(res.roid, id)}.pdf`, res.blob);
    }
    done++;
    // Con varios recorridos el progreso cuenta recorridos, no actuaciones.
    onProgress?.(done, routeIds.length);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const stamp = new Date().toISOString().slice(0, 10);
  triggerDownload(zipBlob, `informes-surveillance-${stamp}.zip`);
}
